import { useEffect, useState } from 'react'
import { passkeysSupported, platformAuthenticatorAvailable, register, signIn, signInWithPin } from '../lib/auth'
import { t } from '../lib/i18n'

/**
 * Sign in, or create an account. Passkeys only.
 *
 * There is no password field and no "forgot password" link, because there is no
 * password. That is stated on the screen rather than left for someone to
 * discover by looking for the missing control.
 */
export default function SignIn({
  onSignedIn,
  startWith = 'in',
  setupOpen = false,
}: {
  onSignedIn: () => Promise<void>
  /**
   * Which form opens first. Somebody who just chose a side on the opening
   * screen has told us they are new — the returning visitor has their own
   * button there — so handing them the sign-in form is making them find the
   * way to the thing they already asked for.
   */
  startWith?: 'in' | 'up'
  /**
   * From /api/health: the owner account has not been claimed yet. The setup
   * token is shown only then; after that it is a box on every stranger's
   * sign-up that nobody but one person ever had a use for.
   */
  setupOpen?: boolean
}) {
  const [mode, setMode] = useState<'in' | 'up' | 'pin'>(startWith)
  const [handle, setHandle] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [setupToken, setSetupToken] = useState('')
  const [pin, setPin] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Whether WebAuthn exists at all decides what is shown; whether there is a
  // BUILT-IN authenticator only decides how the hint is worded.
  const supported = passkeysSupported()
  const [hasPlatform, setHasPlatform] = useState<boolean | null>(null)

  useEffect(() => {
    void platformAuthenticatorAvailable().then(setHasPlatform)
  }, [])

  async function run(fn: () => Promise<unknown>) {
    setBusy(true)
    setError(null)
    try {
      await fn()
      await onSignedIn()
    } catch (err) {
      /*
       * A cancelled ceremony is not an error worth alarming anyone about — the
       * user dismissed a system sheet. Everything else gets the real message.
       */
      const name = (err as { name?: string }).name
      if (name === 'NotAllowedError' || name === 'AbortError') setError(t('auth.cancelled'))
      else setError((err as Error).message)
    } finally {
      setBusy(false)
    }
  }

  if (!supported && mode !== 'pin') {
    return (
      <section className="card w-auth" aria-busy={busy}>
        <p>{t('auth.noPasskeySupport')}</p>
        {/* A device with no WebAuthn at all is exactly the device a delegate
            account exists for, so the way in is offered rather than described. */}
        <button className="link" onClick={() => setMode('pin')}>{t('auth.usePin')}</button>
      </section>
    )
  }

  /*
   * ── The PIN screen ────────────────────────────────────────────────────────
   *
   * Deliberately not a peer of "Sign in": it is one quiet line under the
   * passkey button, because offering a PIN beside a passkey with equal weight
   * would teach people that the weaker credential is the normal one. Anybody
   * who needs it was told about it by whoever created their account.
   */
  if (mode === 'pin') {
    return (
      <section className="card w-auth" aria-busy={busy}>
        <h1>{t('auth.usePin')}</h1>
        <label>
          {t('auth.handle')}
          <input
            value={handle}
            onChange={(e) => setHandle(e.target.value.toLowerCase())}
            autoComplete="username"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            placeholder={t('auth.handlePlaceholder')}
          />
        </label>
        <label>
          {t('auth.pin')}
          <input
            type="password"
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
            // numeric, one-time-code: the keyboard a PIN wants, and iOS will
            // offer a PIN from the password manager if one is stored.
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={12}
          />
        </label>

        <button
          className="primary"
          disabled={busy || !handle || pin.length < 4}
          onClick={() => run(() => signInWithPin(handle, pin))}
        >
          {busy ? t('app.loading') : t('app.signIn')}
        </button>

        {error && <p className="error" role="alert">{error}</p>}

        <p className="muted small">
          <button className="link" onClick={() => { setMode('in'); setError(null) }}>
            {t('auth.usePasskeyInstead')}
          </button>
        </p>
        <p className="muted small">{t('auth.pinHint')}</p>
      </section>
    )
  }

  return (
    <section className="card w-auth" aria-busy={busy}>
      <header className="w-auth-header">
        <h1>{mode === 'in' ? t('app.signIn') : t('auth.createAccount')}</h1>
        <p>{t(mode === 'in' ? 'auth.signInIntro' : 'auth.createIntro')}</p>
      </header>

      <label>
        {t('auth.handle')}
        <input
          value={handle}
          onChange={(e) => setHandle(e.target.value.toLowerCase())}
          autoComplete="username webauthn"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          placeholder={t('auth.handlePlaceholder')}
        />
      </label>

      {mode === 'up' && (
        <>
          <label>
            {t('auth.displayName')}
            <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} autoComplete="name" autoCapitalize="words" />
          </label>
          {setupOpen && (
            <label>
              {t('auth.setupToken')} <span className="muted small">{t('auth.setupOnly')}</span>
              <input value={setupToken} onChange={(e) => setSetupToken(e.target.value)} autoComplete="off" />
            </label>
          )}
        </>
      )}

      <button
        className="primary"
        // A real name, not a fallback to the handle: it is what an employer
        // reads on an application and what a worker reads on a message.
        disabled={busy || (mode === 'up' && (!handle || !displayName.trim()))}
        onClick={() =>
          run(() =>
            mode === 'in'
              ? signIn(handle || undefined)
              : register({ handle, displayName: displayName.trim(), setupToken: (setupOpen && setupToken) || undefined }),
          )
        }
      >
        {busy ? t('app.loading') : mode === 'in' ? t('auth.usePasskey') : t('auth.createAccount')}
      </button>

      {error && <p className="error" role="alert">{error}</p>}

      <p className="w-auth-switch">
        {mode === 'in' ? (
          <>{t('auth.noAccount')} <button className="link" onClick={() => setMode('up')}>{t('auth.createAccount')}</button></>
        ) : (
          <>{t('auth.haveAccount')} <button className="link" onClick={() => setMode('in')}>{t('app.signIn')}</button></>
        )}
      </p>

      {/* One line, under the passkey. A reviewer following the notes finds it;
          nobody choosing between two buttons is taught to prefer the PIN. */}
      <p className="muted small">
        <button className="link" onClick={() => { setMode('pin'); setError(null) }}>
          {t('auth.usePin')}
        </button>
      </p>

      <p className="muted w-auth-help">
        {t('auth.noPassword')} {hasPlatform === false
          ? t('auth.externalPasskey')
          : t('auth.passkeyOnly')}
      </p>
    </section>
  )
}
